import React, { useState } from 'react';
import { Plus, Users } from 'lucide-react';
import SEO from '../components/SEO';
import PageHeader, { PageContainer } from '../components/PageHeader';
import { useParentStudents } from '../features/parentPortal/hooks/useParentStudents';
import StudentCard from '../features/parentPortal/components/StudentCard';
import LinkStudentModal from '../features/parentPortal/components/LinkStudentModal';
import EmptyState from '../features/parentPortal/components/EmptyState';
import ErrorBlock from '../features/parentPortal/components/ErrorBlock';
import { Skeleton, SkeletonCircle, SkeletonPanel, SkeletonRegion } from '../components/ui/Skeleton';
import Button from '../features/parentPortal/components/Button';

export default function ParentDashboardPage() {
  const { students, loading, error, reload, linkStudent } = useParentStudents();
  const [linkOpen, setLinkOpen] = useState(false);

  return (
    <>
      <SEO title="Family" description="Track your students' learning progress on Graspd." path="/family" noindex />
      <PageContainer>
        <PageHeader
          title="Family"
          subtitle="Track your students' progress. Supervising is free — no subscription required."
          actions={
            students.length > 0 && (
              <Button onClick={() => setLinkOpen(true)}>
                <Plus size={16} strokeWidth={1.5} />
                Link a student
              </Button>
            )
          }
        />

        {loading ? (
          <SkeletonRegion
            label="Loading your students"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
              gap: 'var(--space-4)',
            }}
          >
            {Array.from({ length: 2 }, (_, i) => (
              <SkeletonPanel key={i}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
                  <SkeletonCircle size={40} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Skeleton width="62%" height={15} style={{ marginBottom: 6 }} />
                    <Skeleton width="40%" height={12} />
                  </div>
                </div>
                <Skeleton height={6} style={{ marginBottom: 'var(--space-3)' }} />
                <Skeleton width="52%" height={12} />
              </SkeletonPanel>
            ))}
          </SkeletonRegion>
        ) : error ? (
          <ErrorBlock message={error} onRetry={reload} />
        ) : students.length === 0 ? (
          <EmptyState
            icon={<Users size={26} strokeWidth={1.5} />}
            title="Link your first student"
            description="Sign in to your student's account, generate a supervision code from Settings → Sharing, then enter it here. You don't need your own subscription to supervise them."
            action={
              <Button onClick={() => setLinkOpen(true)}>
                <Plus size={16} strokeWidth={1.5} />
                Link a student
              </Button>
            }
          />
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
              gap: 'var(--space-4)',
            }}
          >
            {students.map((s) => (
              <StudentCard key={s.studentId} student={s} />
            ))}
          </div>
        )}
      </PageContainer>

      <LinkStudentModal
        open={linkOpen}
        onClose={() => setLinkOpen(false)}
        onLink={async (code) => {
          await linkStudent(code);
        }}
      />
    </>
  );
}
